import {
  FC,
  Fragment,
  SelectHTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Menu, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { compact, isArray } from 'lodash';
import { GoTriangleDown } from 'react-icons/go';
import { Button } from 'ui/components/common';
import { Checkbox, DateField, Searchbox } from 'ui/components/form';
import { OPTIONS_PROPS, Value } from 'ui/components/nav/Dropdown/Dropdown';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

interface PROPS
  extends Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    'size' | 'onChange' | 'defaultValue'
  > {
  size?: SIZE;
  options: OPTIONS_PROPS[];
  isDateField?: boolean;
  onClear?: () => void;
  startDate?: Date;
  endDate?: Date;
  onChangeDate?: (dates: (Date | undefined)[]) => void;
  optionsClassName?: string;
  selectedClassName?: string;
  withSearch?: boolean;
  onSearch?: (search: string) => void;
  onChange?: (opt: OPTIONS_PROPS) => void;
  defaultValue?: Value;
  isMulti?: boolean;
}

const dropdownButtonClasses = cva(
  [
    'flex items-center justify-between',
    'bg-fill-primary rounded-[6px]',
    'border border-transparent',
    'disabled:bg-fill-secondary disabled:text-disabled',
    'truncate',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['text-xs', 'px-2.5 py-2.5', 'w-[140px] h-9'],
        [SIZE.Medium]: ['text-sm', 'px-2.5 py-3', 'w-[180px] h-[42px]'],
        [SIZE.Large]: ['text-[16px]', 'px-3 py-4', 'w-[220px] h-[52px]'],
      },
      isOpen: {
        true: ['border-fill-overlay'],
        false: [],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
      isOpen: false,
    },
  },
);

const optionsContainerClasses = cva(
  [
    'p-[22px] z-10 bg-fill-tertiary',
    'absolute left-0 origin-top-left z-50',
    'shadow-sm rounded-b-[6px]',
    'space-y-2.5',
    'overflow-y-auto',
    'w-[320px] max-h-[365px]',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: [],
        [SIZE.Medium]: [],
        [SIZE.Large]: [],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

const iconClasses = cva(['text-content'], {
  variants: {
    size: {
      [SIZE.Small]: ['h-4 w-5'],
      [SIZE.Medium]: ['h-5 w-6'],
      [SIZE.Large]: ['h-6 w-7'],
    },
  },
  defaultVariants: {
    size: SIZE.Small,
  },
});

const Filter: FC<PROPS> = ({
  size,
  className,
  options,
  disabled,
  isDateField,
  startDate,
  endDate,
  optionsClassName,
  selectedClassName,
  withSearch,
  onClear,
  onChangeDate,
  onSearch,
  onChange,
  defaultValue,
  placeholder,
  isMulti,
}) => {
  const [selected, setSelected] = useState<Value | undefined>(defaultValue);

  const label = useMemo(() => {
    if (isMulti) {
      const items = isArray(selected)
        ? compact(selected as any[])
        : selected
          ? [selected]
          : [];

      return `${items.length ? `(${items.length})` : ''} ${placeholder}`;
    }

    return selected
      ? options.find((opt) => opt.value === selected)?.label ?? placeholder
      : placeholder;
  }, [isMulti, options, placeholder, selected]);

  useEffect(() => {
    if (typeof defaultValue === 'object') {
      setSelected(compact(defaultValue as any[]));
    } else {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className={cn(className)}>
      <Menu as="div" className="relative inline-block text-left w-full">
        {({ open: isOpen, close }) => (
          <>
            <Menu.Button
              disabled={disabled}
              className={cn(
                dropdownButtonClasses({ size, isOpen }),
                selectedClassName,
              )}
              type="button"
            >
              {label}
              <GoTriangleDown
                className={cn(iconClasses({ size }))}
                aria-hidden="true"
              />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={cn(
                  optionsContainerClasses({ size }),
                  optionsClassName,
                )}
              >
                {(withSearch || isDateField || !!onClear) && (
                  <div className="border-b-[0.5px] border-separator-primary pb-3">
                    {withSearch && (
                      <Searchbox
                        placeholder="Search"
                        onChange={(e) => onSearch?.(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === ' ') e.stopPropagation();
                        }}
                      />
                    )}
                    {isDateField && (
                      <DateField
                        name="date-range"
                        clearSelected={() => {
                          onClear?.();
                          setSelected(undefined);
                        }}
                        selected={startDate ?? new Date()}
                        size="default"
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        placeholder="Custom Date"
                        onChange={(_, dates) => {
                          onChangeDate?.(dates as (Date | undefined)[]);
                        }}
                      />
                    )}
                    {!!onClear && (
                      <Button
                        onClick={() => {
                          onClear();
                          setSelected(undefined);
                        }}
                        label="Clear"
                        intent="link"
                        className="ml-auto px-1 pb-0"
                      />
                    )}
                  </div>
                )}
                {options.map((option, index) => (
                  <Menu.Item
                    disabled={option.disabled || !!endDate}
                    as={'button'}
                    onClick={() => {
                      onChange?.(option);
                      setSelected(option.value);
                      !isMulti && close();
                    }}
                    className={({ active }) =>
                      cn(
                        'flex gap-x-3',
                        'px-5 py-2 w-full text-left',
                        'disabled:text-disabled',
                        'text-content',
                        'hover:bg-fill-primary',
                        active && 'bg-fill-primary',
                        !(option.disabled || !!endDate) &&
                          'hover:text-primary-base',
                        (option.isSelected ||
                          option.value === selected ||
                          (typeof selected === 'object' &&
                            (selected as any[]).includes(option.value))) &&
                          'text-primary-base font-semibold',
                        option.description && 'pb-0 px-[10px]',
                        option.className,
                      )
                    }
                    key={index}
                  >
                    {option.withCheckbox && (
                      <Checkbox
                        checked={option.isSelected || option.value === selected}
                        className="mt-1"
                      />
                    )}
                    <p>
                      <span
                        className={cn(
                          'flex items-center gap-x-2.5',
                          'text-sm',
                          !(option.disabled || !!endDate) &&
                            'hover:font-semibold',
                        )}
                      >
                        {option.iconLeft}
                        <span className="max-w-4/5 truncate">
                          {option.label}
                        </span>
                        {option.iconRight}
                      </span>
                      {option?.description && (
                        <span
                          className={cn(
                            'text-xs',
                            option.disabled || !!endDate
                              ? 'text-disabled'
                              : 'text-content',
                          )}
                        >
                          {option.description}
                        </span>
                      )}
                    </p>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
};

export default Filter;
